import { useState, useEffect } from "react";
import type { ProductBrand } from "@/types";
import {
  createProductBrandRequestSchema,
  updateProductBrandRequestSchema,
} from "@/validation";
import { zodToFieldErrors, firstFieldErrors } from "@trillion/validation";
import { useNetworkOptions } from "@/hooks/use-network-options";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Switch } from "@trillion/ui/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Loader2 } from "lucide-react";

interface BrandFormData {
  source_id: string; // Select value — string in the DOM, number in the payload
  external_id: string;
  name: string;
  active: boolean;
}

interface BrandFormProps {
  initialData?: ProductBrand;
  onSubmit: (data: Partial<ProductBrand>) => Promise<void>;
  isLoading: boolean;
}

function toFormData(brand?: ProductBrand): BrandFormData {
  if (!brand) {
    return { source_id: "", external_id: "", name: "", active: true };
  }
  return {
    source_id: String(brand.source_id),
    external_id: brand.external_id,
    name: brand.name,
    active: brand.active,
  };
}

export function BrandForm({ initialData, onSubmit, isLoading }: BrandFormProps) {
  const [form, setForm] = useState<BrandFormData>(() => toFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { networks, isLoading: networksLoading } = useNetworkOptions();
  const isEdit = initialData != null;

  useEffect(() => {
    if (initialData) {
      setForm(toFormData(initialData));
    }
  }, [initialData]);

  function updateField(field: keyof BrandFormData, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  // source_id is immutable after create (a brand cannot move networks; the update schema has no
  // source_id) — edit renders the network as read-only text.
  const editNetworkLabel =
    isEdit && initialData
      ? (networks.find((n) => n.source_id === initialData.source_id)?.name ??
        String(initialData.source_id))
      : "";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (isEdit) {
      const payload = {
        external_id: form.external_id.trim(),
        name: form.name.trim(),
        active: form.active,
      };
      const result = updateProductBrandRequestSchema.safeParse(payload);
      if (!result.success) {
        setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
        return;
      }
      await onSubmit(payload);
      return;
    }

    if (!form.source_id) {
      setErrors((prev) => ({ ...prev, source_id: "Select a network" }));
      return;
    }
    const payload = {
      source_id: Number(form.source_id),
      external_id: form.external_id.trim(),
      name: form.name.trim(),
      active: form.active,
    };
    const result = createProductBrandRequestSchema.safeParse(payload);
    if (!result.success) {
      setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
      return;
    }
    await onSubmit(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEdit ? "Edit Brand" : "New Brand"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="source_id">Network *</Label>
              {isEdit ? (
                <p className="flex h-9 items-center text-sm text-muted-foreground">
                  {editNetworkLabel} (cannot be changed)
                </p>
              ) : (
                <Select
                  value={form.source_id}
                  onValueChange={(value) => updateField("source_id", value)}
                >
                  <SelectTrigger id="source_id" disabled={networksLoading}>
                    <SelectValue
                      placeholder={networksLoading ? "Loading networks..." : "Select a network"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {networks.map((n) => (
                      <SelectItem key={n.source_id} value={String(n.source_id)}>
                        {n.name}
                        {n.active ? "" : " (inactive)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {errors.source_id && <p className="text-sm text-destructive">{errors.source_id}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="external_id">External ID *</Label>
              <Input
                id="external_id"
                value={form.external_id}
                onChange={(e) => updateField("external_id", e.target.value)}
                placeholder="Advertiser/merchant ID within the network"
              />
              {errors.external_id && (
                <p className="text-sm text-destructive">{errors.external_id}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Brand name"
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="active"
              checked={form.active}
              onCheckedChange={(checked) => updateField("active", checked)}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {isEdit ? "Update Brand" : "Create Brand"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
